import React from 'react';
import HojaCaratula from './base/HojaCaratula';

// Etiquetas EXACTAS de las plantillas (templates/caratula/caratula_reajuste_*.docx): el
// modal es una vista previa, así que lo que se lee acá tiene que coincidir renglón por
// renglón con el papel. `guardable` marca los que además de imprimirse vuelven al
// cliente/trámite (ver CaratulaReajusteDocumentoService.persistirDatosGuardables /
// persistirDatosGuardablesPension).
//
// CAMPOS es la UNIÓN de las CUATRO variantes; cada layout de abajo nombra solo los suyos.
// PENSION_BADARO/PENSION_REPARACION_HISTORICA (beneficio=PENSION) suman el bloque del
// causante, autocompletado desde tramite.causanteXxx, y no llevan estado civil/cónyuge/
// fecha de nacimiento del titular (esas plantillas no las tienen).
const CAMPOS = {
    beneficio: { label: 'REAJUSTAR', tipo: 'beneficio' },
    // Solo PENSION_BADARO/PENSION_REPARACION_HISTORICA: valor libre, sin dónde volver en
    // tramite_reajuste (no hay vinculoBeneficiario como en el módulo Pensión).
    tipoPension: { label: 'REAJUSTAR', tipo: 'texto', nota: 'Valor libre: "DERIVADA", "PENSION DIRECTA", etc., según el reclamo.' },
    numeroBeneficio: { label: 'N° Benef', tipo: 'texto' },
    ley: { label: 'Ley', tipo: 'texto', guardable: true },
    ingreso: { label: 'INGRESO', tipo: 'fecha' },
    pacto: { label: 'PACTO', tipo: 'sino' },

    claveFiscal: { label: 'CLAVE FISCAL', tipo: 'clave', guardable: true },
    claveCvss: { label: 'CLAVE CVSS', tipo: 'clave', guardable: true },
    referencia: { label: 'REFERENCIA', tipo: 'texto' },

    apellidoNombre: { label: 'APELLIDO/NOMBRE TITULAR', tipo: 'texto' },
    dniCuit: { label: 'DNI/CUIT', tipo: 'texto' },
    fechaNacimiento: { label: 'FECHA DE NACIMIENTO', tipo: 'fecha', guardable: true, noFutura: true },
    telefono: {
        label: 'Nº TELEFONICO', tipo: 'texto',
        nota: 'Trae el teléfono propio y los contactos adicionales del cliente. Se editan desde la ficha del cliente.',
    },
    domicilioDniYReal: {
        label: 'DOMICILIO DNI Y REAL', tipo: 'domicilio', campo: 'domicilio', guardable: true,
        estructurado: ['domicilioDniDatos', 'domicilioRealDatos'], etiquetasEstructurado: ['DNI', 'REAL'],
        nota: 'Se guardan en los domicilios DNI y REAL del cliente (el barrio que ya tengan se conserva). Vacío no borra nada.',
    },
    domicilio: {
        label: 'DOMICILIO', tipo: 'domicilio', estructurado: 'domicilioDniDatos', guardable: true,
        nota: 'Se guarda en el domicilio DNI del cliente (el barrio que ya tenga se conserva). Vacío no borra nada.',
    },
    estadoCivil: { label: 'ESTADO CIVIL', tipo: 'estadoCivil', guardable: true },
    conyuge: { label: 'CON', tipo: 'texto', guardable: true },

    // Causante (el fallecido; solo PENSION_BADARO/PENSION_REPARACION_HISTORICA). Autocompletado
    // desde tramite.causanteXxx (ver "Datos del Causante" en la ficha del trámite); lo que se
    // edite acá vuelve al trámite.
    apellidoNombreCausante: { label: 'APELLIDO/NOMBRE CAUSANTE', tipo: 'texto', guardable: true },
    dniCuitCausante: { label: 'DNI/CUIT', tipo: 'texto', guardable: true },
    fechaNacimientoCausante: { label: 'FECHA DE NACIMIENTO', tipo: 'fecha', guardable: true, noFutura: true },
    fechaFallecimientoCausante: { label: 'FECHA DE FALLECIMIENTO', tipo: 'fecha', guardable: true, noFutura: true },

    expedienteReajustar: { label: 'EXPEDIENTE A REAJUSTAR', tipo: 'texto', guardable: true },
    turnoVista: { label: 'TURNO PARA TOMAR VISTA', tipo: 'texto', guardable: true },
    turnoAnses: {
        label: 'DIA TURNO ANSES RECLAMO ADMINISTRATIVO', tipo: 'fecha',
        nota: 'Viene de la fecha de turno ANSES del trámite. Lo que edites acá va solo al documento.',
    },
    conceptosReclamo: { label: 'Conceptos del reclamo', tipo: 'multilinea', guardable: true },
    montosDiferencias: {
        label: 'Montos diferencias', tipo: 'texto', campo: 'montoDiferencia',
        nota: 'Viene de "Diferencia SIN IPC". Lo que edites acá va solo al documento: no modifica el cálculo del trámite.',
    },
    montoDiferencia: {
        label: 'MONTO DIFERENCIA', tipo: 'texto',
        nota: 'Viene de "Diferencia SIN IPC". Lo que edites acá va solo al documento: no modifica el cálculo del trámite.',
    },
    indicePuro: { label: 'Incide puro N°', tipo: 'texto', guardable: true },
    ipcOtros: {
        label: 'IPC (Y otros)', tipo: 'texto',
        nota: 'Viene de "Diferencia CON IPC". Lo que edites acá va solo al documento: no modifica el cálculo del trámite.',
    },
    observaciones: { label: 'OBSERVACIONES', tipo: 'multilinea', guardable: true },
};

// Reajuste tiene CUATRO carátulas. BADARO/REPARACION_HISTORICA las decide el backend a partir
// de tipoReajuste; PENSION_BADARO/PENSION_REPARACION_HISTORICA las elige el operador en el
// menú del botón "Generar Carátula" (beneficio=PENSION no alcanza para derivarlas solo:
// también hacen falta los datos del causante). `datos.variante` dice cuál se dibuja en
// cualquier caso. No son la misma carátula con otro título: Badaro tiene Conceptos / Incide
// puro / IPC y no lleva cónyuge; Reparación Histórica lleva Ley y CON, y un solo renglón de
// monto; las de Pensión suman el causante y no llevan estado civil/cónyuge/fecha de nacimiento.
const LAYOUTS = {
    BADARO: {
        titulo: 'REAJUSTE CASO BADARO',
        cabecera: [['beneficio', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['fechaNacimiento'],
            ['telefono'],
            ['domicilioDniYReal'],
            ['estadoCivil'],
            ['expedienteReajustar'],
            ['turnoVista'],
            ['turnoAnses'],
            ['conceptosReclamo'],
            ['montosDiferencias'],
            ['indicePuro'],
            ['ipcOtros'],
            ['observaciones'],
        ],
    },
    REPARACION_HISTORICA: {
        titulo: 'RECLAMO POR AUMENTOS SOBRE REPARACION HISTORICA',
        cabecera: [['beneficio', 'ley', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['domicilio'],
            ['telefono'],
            ['fechaNacimiento'],
            ['estadoCivil', 'conyuge'],
            ['expedienteReajustar'],
            ['turnoVista'],
            ['montoDiferencia'],
            ['turnoAnses'],
            ['observaciones'],
        ],
    },
    PENSION_BADARO: {
        titulo: 'REAJUSTE CASO BADARO',
        cabecera: [['tipoPension', 'numeroBeneficio', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuitCausante'],
            ['fechaNacimientoCausante'],
            ['fechaFallecimientoCausante'],
            ['telefono'],
            ['expedienteReajustar'],
            ['domicilio'],
            ['turnoVista'],
            ['turnoAnses'],
            ['conceptosReclamo'],
            ['montosDiferencias'],
            ['indicePuro'],
            ['ipcOtros'],
            ['observaciones'],
        ],
    },
    PENSION_REPARACION_HISTORICA: {
        titulo: 'RECLAMO POR AUMENTOS SOBRE REPARACION HISTORICA',
        cabecera: [['tipoPension', 'ley', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuitCausante'],
            ['fechaNacimientoCausante'],
            ['fechaFallecimientoCausante'],
            ['telefono'],
            ['expedienteReajustar'],
            ['domicilio'],
            ['turnoVista'],
            ['montoDiferencia'],
            ['turnoAnses'],
            ['observaciones'],
        ],
    },
};

// CLAVE FISCAL/CVSS y REFERENCIA van igual en las dos y se dibujan aparte, arriba del
// recuadro, tal como están en el papel.
const FILA_CLAVES = ['claveFiscal', 'claveCvss'];
const FILA_REFERENCIA = ['referencia'];

// CLAVE FISCAL/CVSS y REFERENCIA van igual en las dos variantes y se dibujan aparte, arriba
// del recuadro, tal como están en el papel.
const FILAS_PREVIAS = [FILA_CLAVES, FILA_REFERENCIA];

// Ante una variante desconocida se cae a Badaro (COMUN/DOCENTE/OTRO), que es la que usa
// la mayoría de los trámites: preferible una carátula de más que un modal en blanco.
const hojaDe = (form) => {
    const layout = LAYOUTS[form?.variante] || LAYOUTS.BADARO;
    return {
        tituloDoc: layout.titulo,
        cabecera: layout.cabecera,
        filasPrevias: FILAS_PREVIAS,
        cuerpo: layout.cuerpo,
    };
};

/**
 * Modal de la Carátula de Reajuste. Dibuja el layout de la variante que corresponda y
 * muestra los renglones prellenados desde el cliente/trámite, en blanco lo que falta. Al
 * confirmar, persiste los renglones guardables y emite el .docx con el DTO tal cual quedó en
 * el formulario (WYSIWYG).
 */
export default function ModalCaratulaReajuste(props) {
    return (
        <HojaCaratula
            {...props}
            titulo="Generar Carátula de Reajuste"
            nombreDocumento="la Carátula de Reajuste"
            campos={CAMPOS}
            layout={hojaDe}
        />
    );
}
